import React, { type ReactNode } from 'react';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useUserSubscriber } from '@atlassian/jira-user-services/src/main.tsx';
import {
	COLLAPSED_MODE,
	VIEW_MODE,
	OTHER_PROJECT_CALENDARS_EDIT_MODE,
} from '../../common/constants';
import { DEFAULT_TIMEZONE } from '../../common/ui/constants';
// eslint-disable-next-line import/order
import type {
	OtherProjectCalendarListProps,
	OtherProjectCalendarWithSummaryProps,
} from '../../common/utils/reusable-calendars/types';

// eslint-disable-next-line import/order
import { CalendarContainer } from '../calendars';

// eslint-disable-next-line import/order
import { CalendarView } from '../calendars/calendar';

import { CalendarForm } from '../calendars/editable';
import { OtherProjectCalendarsSummary } from './summary-with-actions';

export const OtherProjectCalendarWithSummary = ({
	calendar,
	permissionsWithTimezone,
	isSelected,
	selectedCalendarMode,
	calendarsActions,
}: OtherProjectCalendarWithSummaryProps) => {
	const isJiraAdmin = useIsAdmin();

	const [{ data: loggedInUser }] = useUserSubscriber();

	const { id } = calendar;
	const {
		onToggleViewRequested,
		onSelectThisCalendarTab,
		onCopyCalendar,
		onEditCalendarSuccessful,
		onEditRequested,
		onReset,
		onDeleteCalendarSuccessful,
	} = calendarsActions;
	const mode = isSelected ? selectedCalendarMode : COLLAPSED_MODE;
	const userTimezone: string = (loggedInUser && loggedInUser.timeZone) ?? DEFAULT_TIMEZONE;

	const dataTestId = `servicedesk-sla-settings.ui.reusable-sla-calendars.calendar-${id}`;
	switch (mode) {
		case VIEW_MODE: {
			return (
				<CalendarContainer data-test-id={dataTestId}>
					<OtherProjectCalendarsSummary
						calendar={calendar}
						onToggleDetails={() => onToggleViewRequested(id)}
						onSelectThisCalendarTab={onSelectThisCalendarTab}
						onCopyCalendar={onCopyCalendar}
						canCopyCalendar={permissionsWithTimezone.canCreate}
						onEditRequested={() => onEditRequested(id)}
						onDeleteCalendarSuccessful={onDeleteCalendarSuccessful}
						isJiraAdmin={isJiraAdmin}
						userTimeZone={userTimezone}
						isOpen
					/>
					<CalendarView calendar={calendar} permissionsWithTimezone={permissionsWithTimezone} />
				</CalendarContainer>
			);
		}

		case OTHER_PROJECT_CALENDARS_EDIT_MODE: {
			return (
				<CalendarContainer data-test-id={dataTestId}>
					<CalendarForm
						allTimezones={permissionsWithTimezone.allTimezones}
						defaultTimezone={permissionsWithTimezone.defaultTimezone}
						calendarData={calendar}
						onCancel={onReset}
						onEditCalendarSuccessful={onEditCalendarSuccessful}
						// @ts-expect-error - TS2322 - Type '"other_project_calendars_edit_mode"' is not assignable to type 'Mode'.
						calendarMode={mode}
					/>
				</CalendarContainer>
			);
		}

		default: {
			return (
				<CalendarContainer data-test-id={dataTestId}>
					<OtherProjectCalendarsSummary
						calendar={calendar}
						onToggleDetails={() => onToggleViewRequested(id)}
						onSelectThisCalendarTab={onSelectThisCalendarTab}
						onCopyCalendar={onCopyCalendar}
						canCopyCalendar={permissionsWithTimezone.canCreate}
						onEditRequested={() => onEditRequested(id)}
						onDeleteCalendarSuccessful={onDeleteCalendarSuccessful}
						isJiraAdmin={isJiraAdmin}
						userTimeZone={userTimezone}
						isOpen={false}
					/>
				</CalendarContainer>
			);
		}
	}
};

export const OtherProjectCalendarList = ({
	calendars,
	permissionsWithTimezone,
	selectedCalendarMode,
	calendarsActions,
	selectedCalendarId,
}: OtherProjectCalendarListProps) => (
	<>
		{calendars.reduce<ReactNode[]>((calendarList, calender, calenderKey) => {
			calendarList.push(
				<OtherProjectCalendarWithSummary
					key={calenderKey}
					calendar={calender}
					selectedCalendarMode={selectedCalendarMode}
					isSelected={selectedCalendarId === calender.id}
					permissionsWithTimezone={permissionsWithTimezone}
					calendarsActions={calendarsActions}
				/>,
			);

			return calendarList;
		}, [])}
	</>
);
